import React, { useState } from 'react';
import axios from 'axios';
import { previousHeatingMethod, heaterMaintenance } from './config';
import RenderError from '../../components/RenderError';
import Success from '../../../equipmentSurveys/components/Success';
import Submit from '../../../equipmentSurveys/components/Submit';

function CurrentPractices({
  applicationData: {
    aid,
    heatingMethod,
    numberOfHeaters,
    spaceHeatingSystem,
    hoursPerYear,
    consumptionPerYear,
    energyCost,
    maintenance,
    maintenanceProvider
  },
  setApplicationData,
  initialView,
  setView
}) {
  const [formData, setFormData] = useState({
    aid: aid ?? '',
    heatingMethod: heatingMethod ?? {},
    numberOfHeaters: numberOfHeaters ? parseInt(numberOfHeaters) : 0,
    spaceHeatingSystem: spaceHeatingSystem ?? '',
    hoursPerYear: hoursPerYear ? parseInt(hoursPerYear) : 0,
    consumptionPerYear: consumptionPerYear ?? '',
    energyCost: energyCost ? parseFloat(energyCost) : 0,
    maintenance: maintenance ?? {},
    maintenanceProvider: maintenanceProvider ?? ''
  });
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const handleCheckHeatingMethod = (event) => {
    const { value } = event.target;

    if (value in formData.heatingMethod) {
      let newFormData = formData.heatingMethod;
      delete newFormData[value];
      setFormData({ ...formData, heatingMethod: newFormData });
    } else {
      setFormData({
        ...formData,
        heatingMethod: {
          ...formData.heatingMethod,
          [value]: value === 'Other (please specify)' ? '' : value
        }
      });
    }
  };

  const handleCheckTask = (event) => {
    const { value } = event.target;

    if (value in formData.maintenance) {
      let newFormData = formData.maintenance;
      delete newFormData[value];
      setFormData({ ...formData, maintenance: newFormData });
    } else {
      setFormData({
        ...formData,
        maintenance: { ...formData.maintenance, [value]: '' }
      });
    }
  };

  const setMaintenanceInterval = (event, task) => {
    const { value } = event.target;
    setFormData({
      ...formData,
      maintenance: { ...formData.maintenance, [task]: value }
    });
  };

  const handleInputChange = (event) => {
    const { value, name } = event.target;

    if (name.includes('_other')) {
      let key = name.replace('_other', '');
      setFormData({
        ...formData,
        [key]: {
          ...formData[key],
          ['Other (please specify)']: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    setErrors([]);

    axios
      .post(
        window.wlPipConfig.ajaxUrl +
          '?action=wl_fip_save_building_heat_current_practices',
        formData
      )
      .then((res) => {
        setLoading(false);
        setSuccess(res.data.data.message);
        setApplicationData(res.data.data.applicationData);
        setTimeout(() => setView(initialView), 1000);
      })
      .catch((err) => {
        setLoading(false);
        setErrors(err.response.data.data);
      });
  };

  return (
    <div className="fip-unit-form">
      <div className="fip-unit-form-fields">
        <div className="grid__col-xs-12 u--padding-left-0">
          <div className="form__field form__field--required">
            <label className="form__label">
              Before participating in the Propane Farm Incentive Program, how
              did you heat the site where you plan to install your
              propane-fueled heater? (Check all that apply.)
            </label>
            {errors.heatingMethod && (
              <RenderError error={errors.heatingMethod} />
            )}
            {Object.values(previousHeatingMethod).map((method) => (
              <div key={method}>
                <label className="form__checkbox">
                  <input
                    type="checkbox"
                    value={method}
                    checked={method in formData.heatingMethod}
                    onChange={handleCheckHeatingMethod}
                  />
                  {method}
                </label>
                {method in formData.heatingMethod &&
                  (method === 'Other (please specify)' ? (
                    <div className="u--margin-top-10 u--margin-left-10">
                      <input
                        type="text"
                        name="heatingMethod_other"
                        value={formData.heatingMethod[method]}
                        onChange={handleInputChange}
                        className="form__input form__input--lg u--width-100"
                      />
                      {errors[method] && <RenderError error={errors[method]} />}
                    </div>
                  ) : (
                    ''
                  ))}
              </div>
            ))}
          </div>
          <div className="form__field form__field--required">
            <label className="form__label">
              How many building heaters did you/do you own before purchasing
              your new propane-fueled heater? (Please enter 0 if none)
            </label>
            <input
              type="number"
              className="form__input form__input--lg"
              value={formData.numberOfHeaters}
              name="numberOfHeaters"
              onChange={handleInputChange}
            />
            {errors.numberOfHeaters && (
              <RenderError error={errors.numberOfHeaters} />
            )}
          </div>
          <div className="form__field form__field--required">
            <label className="form__label">
              Please provide the following information about your current
              building heaters:
            </label>
            <span className="u--margin-right-10">
              What type of space heating system did you operate? (e.g., forced
              air furnace, radiant heating)
            </span>
            <input
              type="text"
              value={formData.spaceHeatingSystem}
              className="form__input form__input--lg u--margin-bottom-10"
              name="spaceHeatingSystem"
              onChange={handleInputChange}
            />
            {errors.spaceHeatingSystem && (
              <RenderError error={errors.spaceHeatingSystem} />
            )}
            <span className="u--margin-right-10">
              On Average, how many hours did your system operate in a typical
              year?
            </span>
            <input
              type="number"
              value={formData.hoursPerYear}
              className="form__input form__input--lg u--margin-bottom-10"
              name="hoursPerYear"
              onChange={handleInputChange}
            />
            {errors.hoursPerYear && <RenderError error={errors.hoursPerYear} />}
            <span className="u--margin-right-10">
              On average, how much total fuel/energy does the space heating
              system consume in a typical year?
            </span>
            <input
              type="text"
              value={formData.consumptionPerYear}
              className="form__input form__input--lg u--margin-bottom-10"
              name="consumptionPerYear"
              onChange={handleInputChange}
            />
            {errors.consumptionPerYear && (
              <RenderError error={errors.consumptionPerYear} />
            )}
            <span className="u--margin-right-10">
              What was the average price per unit of fuel/energy during the past
              year?
            </span>
            <input
              type="number"
              step=".01"
              value={formData.energyCost}
              className="form__input form__input--lg u--margin-bottom-10"
              name="energyCost"
              onChange={handleInputChange}
            />
            {errors.energyCost && <RenderError error={errors.energyCost} />}
          </div>
          <div className="form__field form__field--required">
            <label className="form__label">
              What type of maintenance do your heaters typically require each
              year and how often? (select all that apply)
            </label>
            {errors.maintenance && <RenderError error={errors.maintenance} />}
            {Object.entries(heaterMaintenance.questions).map((task) => (
              <div key={task[0]}>
                <label className="form__checkbox">
                  <input
                    type="checkbox"
                    value={task[0]}
                    checked={task[0] in formData.maintenance}
                    onChange={handleCheckTask}
                  />
                  {task[1]}
                </label>
                {task[0] in formData.maintenance && (
                  <div className="form__select u--margin-top-10 u--margin-left-10">
                    {task[0] !== 'other' ? (
                      <select
                        defaultValue={formData.maintenance[task[0]]}
                        onChange={(event) =>
                          setMaintenanceInterval(event, task[0])
                        }
                      >
                        <option value="">Frequency of maintenance</option>
                        {Object.entries(heaterMaintenance.selectOptions).map(
                          (option) => (
                            <option key={option[0]} value={option[0]}>
                              {option[1]}
                            </option>
                          )
                        )}
                      </select>
                    ) : (
                      <div className="form__field form__field--required u--margin-bottom-0">
                        <textarea
                          className="form__textarea"
                          value={formData.maintenance[task[0]]}
                          name="maintenance_other"
                          onChange={(event) =>
                            setMaintenanceInterval(event, task[0])
                          }
                          placeholder="Please Specify"
                        />
                      </div>
                    )}
                    {errors[task[0]] && <RenderError error={errors[task[0]]} />}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="form__field form__field--required">
            <label
              className="form__label u--margin-right-20"
              htmlFor="maintenanceProvider"
            >
              Who typically performs heater maintenance? (select one)
            </label>
            {errors.maintenanceProvider && (
              <RenderError error={errors.maintenanceProvider} />
            )}
            <label className="form__radio">
              <input
                type="radio"
                name="maintenanceProvider"
                value="company"
                onChange={handleInputChange}
                checked={
                  formData.maintenanceProvider === 'company' && 'checked'
                }
              />
              My company or employee(s)
            </label>
            <label className="form__radio">
              <input
                type="radio"
                name="maintenanceProvider"
                value="dealership"
                onChange={handleInputChange}
                checked={
                  formData.maintenanceProvider === 'dealership' && 'checked'
                }
              />
              My dealership or local service center
            </label>
          </div>
          <Submit
            loading={loading}
            handleSubmit={handleSubmit}
            text="Save Section"
            errors={errors}
            disabled={!Boolean(aid)}
          />
          {success && <Success success={success} />}
        </div>
      </div>
    </div>
  );
}

CurrentPractices.displayName = 'CurrentPractices'; // because WebPack renames the function
export default CurrentPractices;
